/* eslint-disable array-element-newline */
/* eslint-disable indent */
/* eslint-disable max-lines-per-function */
import {debounced, decodeEntities} from '@mvps-genesis/utilities';
import 'extensions/rma/components/x-rma-product';
import 'extensions/rma/components/x-rma-review-summary';
import 'extensions/rma/components/x-rma-summary';
import 'extensions/rma/components/x-rma-summary-product';
import {DEFAULT_RMA_STATE, INVALID_INVOICE_NUMBER_ERROR, ITEM_CONDITIONS, SEND_RETURN_RESPONSE_ERROR, STATE_ID_QUERY_PARAM, Steps} from 'extensions/rma/data/constants';
import {RMAController} from 'extensions/rma/data/rma-controller';
import {rmaStore} from 'extensions/rma/data/rma-store';
import 'extensions/ui-components/x-address';
import 'extensions/ui-components/x-address-list-input';
import 'extensions/ui-components/x-date-input';
import 'extensions/ui-components/x-loader';
import 'extensions/ui-components/x-message';
import {defineCustomElement, formatDate, formatPrice} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import stylesheet from './x-rma.scss?as=stylesheet';

export class RMA extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			ajaxProcessing: {
				state: true,
				type: Boolean
			},
			controller: {
				state: true,
				type: Object
			},
			currentDate: {
				state: true,
				type: String
			},
			errorMessage: {
				state: true,
				type: String
			},
			globalErrorMessage: {
				state: true,
				type: String
			},
			invoice: {
				type: Number
			},
			loading: {
				state: true,
				type: Boolean
			},
			mivaCustomerEmail: {
				attribute: 'miva-customer-email',
				type: String
			},
			mivaOrderId: {
				attribute: 'miva-order-id',
				type: Number
			},
			returnReasons: {
				attribute: 'return-reasons',
				converter: value => JSON.parse(decodeEntities(value)),
				type: Array
			},
			state: {
				state: true,
				type: Object
			}
		};
	}

	get internalForm () {
		return this.renderRoot?.querySelector('form');
	}

	constructor () {
		super();

		// Internal state/properties

		/**
		 * @type {boolean}
		 */
		this.ajaxProcessing = false;

		/**
		 * @type {RMAController}
		 */
		this.controller = new RMAController();

		/**
		 * @type {string}
		 */
		this.currentDate = formatDate(new Date(), {
			days: 1
		});

		/**
		 * @type {string}
		 */
		this.errorMessage = '';

		/**
		 * @type {string}
		 */
		this.globalErrorMessage = '';

		/**
		 * @type {boolean}
		 */
		this.loading = true;

		/**
		 * @type {import('extensions/rma/types').Reason[]}
		 */
		this.returnReasons = [];

		/**
		 * @type {import('extensions/rma/types').RMAState}
		 */
		this.state = this._getStateFromUrl();

		// Default properties

		/**
		 * @type {number}
		 */
		this.invoice = 0;

		/**
		 * @type {string}
		 */
		this.mivaCustomerEmail = '';

		/**
		 * @type {number}
		 */
		this.mivaOrderId = 0;

		// Bind handlers
		this.handlePopstate = debounced(200, this._handlePopstate.bind(this));
		this.handleStateChange = debounced(50, this._handleStateChange.bind(this));
	}

	async connectedCallback () {
		super.connectedCallback();

		// Add event listeners
		this.controller.addEventListener('http', () => rmaStore.clear());
		window.addEventListener('popstate', this.handlePopstate);

		// eslint-disable-next-line array-element-newline
		const [, errorMessage] = await this.controller.getInvoice(this.invoice);

		// Set error message property if error exists
		if (errorMessage) {
			this.globalErrorMessage = INVALID_INVOICE_NUMBER_ERROR;

			// Clear out session storage to prevent caching old or invalid data
			rmaStore.clear();

			return;
		}

		// Set loading to false which fully renders the template
		this.loading = false;

		// Add listener for handling state change
		this.addEventListener('statechange', this.handleStateChange);
	}

	disconnectedCallback () {
		window.removeEventListener('popstate', this.handlePopstate);

		super.disconnectedCallback();
	}

	_getStateFromUrl () {
		const url = new URL(window.location.href);
		const stateId = url.searchParams.get(STATE_ID_QUERY_PARAM);

		return rmaStore.get(stateId, DEFAULT_RMA_STATE);
	}

	_setUrlFromState () {
		const url = new URL(window.location.href);

		// Only set history if state id does not match
		if (url.searchParams?.get(STATE_ID_QUERY_PARAM) !== this.state?.id) {
			url.searchParams.set(STATE_ID_QUERY_PARAM, this.state.id);
			window.history.pushState(this.state.id, '', url.toString());
		}
	}

	_handlePopstate () {
		this.state = this._getStateFromUrl();
	}

	async _handleStateChange () {
		await this.updateComplete;

		/**
		 * @type {import('extensions/rma/types').RMAProductState[]}
		 */
		const productStates = (Array.from(this.renderRoot?.querySelectorAll('x-rma-product')) || [])
			?.map(product => product.state)
			?.filter(productState => productState.selected);

		this.updateState({
			mivaCustomerEmail: this.mivaCustomerEmail,
			mivaOrderId: this.mivaOrderId,
			productStates,
			subTotal: productStates?.reduce((subTotal, productState) => subTotal + productState.subTotal, 0),
			tax: productStates?.reduce((tax, productState) => tax + productState.tax, 0),
			total: productStates?.reduce((subTotal, productState) => subTotal + productState.subTotal, 0) + productStates?.reduce((tax, productState) => tax + productState.tax, 0),
			valid: productStates.length > 0 && productStates?.every(productState => productState.valid)
		});
	}

	/**
	 * @param {import('extensions/rma/types').RMAState} [newState]
	 */
	updateState (newState = {}) {
		this.errorMessage = '';
		this.state = rmaStore.set(newState, true);

		this._setUrlFromState();
	}

	determineNextStep () {
		switch (this.state?.step) {
			default:
			case Steps.SelectProducts:
				return Steps.ShippingPayment;
			case Steps.ShippingPayment:
				return Steps.Review;
		}
	}

	async setStep () {
		this.updateState({
			step: this.determineNextStep()
		});

		await this.updateComplete;

		window.scrollTo({
			left: 0,
			top: 0
		});
	}

	handleSummarySubmit () {
		this.setStep();
	}

	async handleShippingPaymentFormSubmit (e) {
		e.preventDefault();

		const formData = Object.fromEntries(new FormData(e.target).entries() ?? []);

		// eslint-disable-next-line array-element-newline
		const [fname, lname] = formData.name?.split(' ');

		this.updateState({
			packageHeight: formData.packageHeight,
			packageLength: formData.packageLength,
			packageWidth: formData.packageWidth,
			pickupLocation: {
				addr1: formData.addr1,
				addr2: formData.addr2,
				city: formData.city,
				cntry: formData.cntry,
				comp: formData.comp,
				email: formData.email,
				fax: formData.fax,
				fname,
				id: formData.id,
				lname,
				phone: formData.phone,
				state: formData.state,
				zip: formData.zip
			},
			specialInstructions: formData.specialInstructions
		});

		this.ajaxProcessing = true;

		// Run ajax call to send return data
		let errorMessage = '';
		if (this.mivaOrderId) {
			// eslint-disable-next-line array-element-newline
			[, errorMessage] = await this.controller.sendReturnMiva(this.state);
		}
		else {
			// eslint-disable-next-line array-element-newline
			[, errorMessage] = await this.controller.sendReturnNonMiva(this.state);
		}

		this.ajaxProcessing = false;

		// If error message render error, else update step to review
		if (errorMessage) {
			this.errorMessage = SEND_RETURN_RESPONSE_ERROR;

			await this.updateComplete;

			window.scrollTo({
				behavior: 'smooth',
				left: 0,
				top: 0
			});
		}
		else {
			this.updateState({
				step: Steps.Review
			});
		}
	}

	renderProducts () {
		return this.controller?.store?.state?.invcDtls?.map(product => {
			const stateProduct = this.state?.productStates?.find(_stateProduct => _stateProduct.lineId === product.invoiceLine && _stateProduct.sku === product.partNum);

			return html`
				<x-rma-product
					?selected="${stateProduct?.selected}"
					.returnData="${stateProduct?.returnData}"
					quantity="${stateProduct?.quantity ?? 1}"
					.reasons="${this.returnReasons}"

					?disabled="${product.currentReturnStatus === 'RMARaised'}"
					image-url="data:image/png;base64,${product.productImage}"
					line-id="${product.invoiceLine}"
					category="${product.lineDesc}"
					price="${product.unitPrice}"
					tax="${product.totalTax / product.ourShipQty}"
					sku="${product.partNum}"
					total-quantity="${product.ourShipQty}"
					.serialNumbers="${product.serialNumbers?.filter(_serialNumber => _serialNumber)}"
				></x-rma-product>
			`;
		});
	}

	renderSummaryProducts () {
		return this.state?.productStates?.map(stateProduct => {
			const product = this.controller?.store?.state?.invcDtls?.find(_product => parseInt(stateProduct.lineId, 10) === parseInt(_product.invoiceLine, 10) && String(stateProduct.sku) === String(_product.partNum));

			return stateProduct?.returnData?.map(returnData => {
				const foundReason = this.returnReasons.find(_reason => _reason.code === returnData.reason);
				const itemCondition = ITEM_CONDITIONS?.find(_itemCondition => _itemCondition.code === returnData.itemCondition);

				return html`
					<x-rma-summary-product
						slot="product"
						serial-number="${returnData.serialNumber}"
						reason="${foundReason?.description}"
						item-condition="${itemCondition?.description}"
						.uploadData="${returnData.uploadData}"
						quantity="${stateProduct?.quantity}"
						image-url="data:image/png;base64,${product.productImage}"
						line-id="${product.invoiceLine}"
						category="${product.lineDesc}"
						price="${product.unitPrice}"
						sku="${product.partNum}"
					></x-rma-summary-product>
				`;
			});
		}).flat();
	}

	renderStepSelectProducts () {
		return html`
			<div class="step wrapper step--select-products">
				<header>
					<h1 class="title">Select Items to Return</h1>
					<div class="subtitle">Invoice #: ${this.invoice}</div>
				</header>
				<main>
					<div class="content">${this.renderProducts()}</div>
					<div class="summary">
						<x-rma-summary
							class="sticky"
							tax="${this.state?.tax}"
							sub-total="${this.state?.subTotal}"
							total="${this.state?.total}"
							?valid="${this.state?.valid}"
							@rma-summary:submit="${this.handleSummarySubmit}"
						>
							<slot name="helpful-info" slot="helpful-info"></slot>
						</x-rma-summary>
					</div>
				</main>
			</div>
		`;
	}

	renderStepShippingPayment () {
		return html`
			<div class="step wrapper step--shipping-payment">
				${this.errorMessage ? html`<x-message type="error">${this.errorMessage}</x-message>` : nothing}
				<header>
					<h1 class="title">Return Details</h1>
				</header>
				<main>
					<div class="content">
						<form @submit="${this.handleShippingPaymentFormSubmit}">
							<section>
								<h2 class="section-title">Pickup Location</h2>

								<div class="pickup-location-container">
									<x-address-list-input name="return-address" tabindex="0" customer="${window.mivaJS.Customer_ID}" .address="${this.state?.pickupLocation}"></x-address-list-input>
								</div>
							</section>

							<section>
								<h2 class="section-title">Pickup Date</h2>
								<div class="section-subtitle">We will contact you prior to pickup and confirm time and date.</div>

								<div class="wrap-440">
									<label for="l-special-instructions" class="label">Special Instructions (Optional)</label>
									<textarea id="l-special-instructions" name="specialInstructions" class="special-instructions-textarea c-form-input">${this.state?.specialInstructions}</textarea>
								</div>
							</section>

							<section>
								<h2 class="section-title">Return Credit to</h2>

								<label class="c-form-checkbox c-form-checkbox--radio">
									<input name="paymentMethod" class="c-form-checkbox__input" type="radio" name="payment" value="Original Payment Method" checked />
									<div class="t-checkout-caption">
										<span class="c-form-checkbox__caption">
											<span class="t-checkout-caption__inner">
												Original Payment Method
											</span>
										</span>
									</div>
								</label>
							</section>

							<section class="total-container">
								<div class="total">Estimated Credit: ${formatPrice(this.state?.total)}</div>

								<button class="confirm-return-button" ?disabled="${this.ajaxProcessing}">${this.ajaxProcessing ? 'Loading...' : 'Confirm Return'}</button>
							</section>
						</form>
					</div>
					<div class="summary">
						<x-rma-summary
							class="sticky"
							tax="${this.state?.tax}"
							sub-total="${this.state?.subTotal}"
							total="${this.state?.total}"
							disable-button
						>
							${this.renderSummaryProducts()}
							<slot name="helpful-info" slot="helpful-info"></slot>
						</x-rma-summary>
					</div>
				</main>
			</div>
		`;
	}

	renderStepsReview () {
		return html`
			<div class="step step--review">
				<div class="wrapper">
					<header>
						<h1 class="title">Return Request Confirmed</h1>
					</header>

					<main>
						<div class="review-summary">
							<x-rma-review-summary
								tax="${this.state?.tax}"
								sub-total="${this.state?.subTotal}"
								total="${this.state?.total}"
								disable-button
							>
								${this.renderSummaryProducts()}
							</x-rma-review-summary>
						</div>

						<div class="review-info">
							<section>
								<h2 class="section-title">Return Credit to</h2>

								<div class="section-copy">Original payment method</div>
							</section>

							${
								this.state?.packageHeight || this.state?.packageLength || this.state?.packageLength
									? html`
										<section>
											<h2 class="section-title">Package Dimensions</h2>

											<div class="section-copy">
												${
													[
														['Height', this.state?.packageHeight],
														['Length', this.state?.packageLength],
														['Width', this.state?.packageWidth]
													]
														.filter(([, value]) => value)
														.map(([label, value]) => `${label}: ${value}cm`)
														.join(', ')
												}
											</div>
										</section>
									`
									: nothing
							}

							<section>
								<h2 class="section-title">Pickup Address</h2>

								<x-address class="address">
									<div slot="first-name">${this.state?.pickupLocation?.fname}</div>
									<div slot="last-name">${this.state?.pickupLocation?.lname}</div>

									<div slot="address">${this.state?.pickupLocation?.addr1}</div>
									${this.state?.pickupLocation?.addr2 ? html`<div slot="address">${this.state?.pickupLocation?.addr2}</div>` : nothing}
									<div slot="city">${this.state?.pickupLocation?.city}</div>
									<div slot="state">${this.state?.pickupLocation?.state}</div>
									<div slot="zip">${this.state?.pickupLocation?.zip}</div>
									<div slot="country">${this.state?.pickupLocation?.cntry}</div>
								</x-address>
							</section>

							<section>
								<h2 class="section-title">Pickup Date</h2>

								${
									this.state?.specialInstructions
										? html`
											<br>
											<div class="section-copy">${this.state?.specialInstructions}</div>
										`
										: nothing
								}
							</section>

							<section>
								<h2 class="section-title">Helpful Info</h2>

								<slot name="helpful-info"></slot>
							</section>
						</div>
					</main>
				</div>
			</div>
		`;
	}

	renderGlobalErrorMessage () {
		return html`<div class="error-container wrapper">
			<x-message type="error">${this.globalErrorMessage}</x-message>
		</div>`;
	}

	renderSteps () {
		switch (this.state?.step) {
			default:
			case Steps.SelectProducts:
				return this.renderStepSelectProducts();
			case Steps.ShippingPayment:
				return this.renderStepShippingPayment();
			case Steps.Review:
				return this.renderStepsReview();
		}
	}

	render () {
		if (this.globalErrorMessage) {
			return this.renderGlobalErrorMessage();
		}

		if (this.loading) {
			return html`<x-loader></x-loader>`;
		}

		return this.renderSteps();
	}
};

defineCustomElement('x-rma', RMA);
